import { apiCall } from '../../_services/apiCall';
import config from '../../config/config';

const BASE = `${config.POSTLOGIN_API_URL_COMPANY}carbon-sequestration`;

/* ─────────────────────────────────────────────
   HELPER — strip undefined / null for clean payloads
───────────────────────────────────────────── */
const clean = obj =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ''));

/* ═══════════════════════════════════════════════════════
   PROJECTS
═══════════════════════════════════════════════════════ */
export const getProjects = async () => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/projects`, {}, {}, 'GET');
    return isSuccess ? data : [];
  } catch (e) { console.error('getProjects:', e); return []; }
};

export const getProjectById = async (id) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/projects/${id}`, {}, {}, 'GET');
    return isSuccess ? data : null;
  } catch (e) { console.error('getProjectById:', e); return null; }
};

export const createProject = async (dto) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/projects`, {}, clean(dto), 'POST');
    return isSuccess ? data : null;
  } catch (e) { console.error('createProject:', e); return null; }
};

export const updateProject = async (id, dto) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/projects/${id}`, {}, clean(dto), 'PATCH');
    return isSuccess ? data : null;
  } catch (e) { console.error('updateProject:', e); return null; }
};

export const deleteProject = async (id) => {
  try {
    const { isSuccess } = await apiCall(`${BASE}/projects/${id}`, {}, {}, 'DELETE');
    return isSuccess;
  } catch (e) { console.error('deleteProject:', e); return false; }
};

/* ═══════════════════════════════════════════════════════
   PLOTS
═══════════════════════════════════════════════════════ */
export const getPlots = async (projectId) => {
  try {
    const params = projectId ? { projectId } : {};
    const { isSuccess, data } = await apiCall(`${BASE}/plots`, {}, params, 'GET');
    return isSuccess ? data : [];
  } catch (e) { console.error('getPlots:', e); return []; }
};

export const getPlotById = async (id) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/plots/${id}`, {}, {}, 'GET');
    return isSuccess ? data : null;
  } catch (e) { console.error('getPlotById:', e); return null; }
};

export const createPlot = async (dto) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/plots`, {}, clean(dto), 'POST');
    return isSuccess ? data : null;
  } catch (e) { console.error('createPlot:', e); return null; }
};

export const updatePlot = async (id, dto) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/plots/${id}`, {}, clean(dto), 'PATCH');
    return isSuccess ? data : null;
  } catch (e) { console.error('updatePlot:', e); return null; }
};

export const deletePlot = async (id) => {
  try {
    const { isSuccess } = await apiCall(`${BASE}/plots/${id}`, {}, {}, 'DELETE');
    return isSuccess;
  } catch (e) { console.error('deletePlot:', e); return false; }
};

/* ═══════════════════════════════════════════════════════
   STRATA
═══════════════════════════════════════════════════════ */
export const getStrata = async (projectId) => {
  try {
    const params = projectId ? { projectId } : {};
    const { isSuccess, data } = await apiCall(`${BASE}/stratum`, {}, params, 'GET');
    return isSuccess ? data : [];
  } catch (e) { console.error('getStrata:', e); return []; }
};

export const getStratumById = async (id) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/stratum/${id}`, {}, {}, 'GET');
    return isSuccess ? data : null;
  } catch (e) { console.error('getStratumById:', e); return null; }
};

export const createStratum = async (dto) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/stratum`, {}, clean(dto), 'POST');
    return isSuccess ? data : null;
  } catch (e) { console.error('createStratum:', e); return null; }
};

export const updateStratum = async (id, dto) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/stratum/${id}`, {}, clean(dto), 'PATCH');
    return isSuccess ? data : null;
  } catch (e) { console.error('updateStratum:', e); return null; }
};

export const deleteStratum = async (id) => {
  try {
    const { isSuccess } = await apiCall(`${BASE}/stratum/${id}`, {}, {}, 'DELETE');
    return isSuccess;
  } catch (e) { console.error('deleteStratum:', e); return false; }
};

/* ═══════════════════════════════════════════════════════
   TREES
═══════════════════════════════════════════════════════ */
export const getTreesByPlot = async (plotId) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/trees`, {}, { plotId }, 'GET');
    return isSuccess ? data : [];
  } catch (e) { console.error('getTreesByPlot:', e); return []; }
};

export const createTree = async (dto) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/trees`, {}, clean(dto), 'POST');
    return isSuccess ? data : null;
  } catch (e) { console.error('createTree:', e); return null; }
};

export const updateTree = async (id, dto) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/trees/${id}`, {}, clean(dto), 'PATCH');
    return isSuccess ? data : null;
  } catch (e) { console.error('updateTree:', e); return null; }
};

export const deleteTree = async (id) => {
  try {
    const { isSuccess } = await apiCall(`${BASE}/trees/${id}`, {}, {}, 'DELETE');
    return isSuccess;
  } catch (e) { console.error('deleteTree:', e); return false; }
};

/* ═══════════════════════════════════════════════════════
   SOIL SAMPLES
═══════════════════════════════════════════════════════ */
export const getSoilSamplesByPlot = async (plotId) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/soil-samples`, {}, { plotId }, 'GET');
    return isSuccess ? data : [];
  } catch (e) { console.error('getSoilSamplesByPlot:', e); return []; }
};

export const createSoilSample = async (dto) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/soil-samples`, {}, clean(dto), 'POST');
    return isSuccess ? data : null;
  } catch (e) { console.error('createSoilSample:', e); return null; }
};

export const updateSoilSample = async (id, dto) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/soil-samples/${id}`, {}, clean(dto), 'PATCH');
    return isSuccess ? data : null;
  } catch (e) { console.error('updateSoilSample:', e); return null; }
};

export const deleteSoilSample = async (id) => {
  try {
    const { isSuccess } = await apiCall(`${BASE}/soil-samples/${id}`, {}, {}, 'DELETE');
    return isSuccess;
  } catch (e) { console.error('deleteSoilSample:', e); return false; }
};

/* ═══════════════════════════════════════════════════════
   CARBON STOCK CALCULATIONS
═══════════════════════════════════════════════════════ */
export const getCarbonStockByPlot = async (plotId) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/carbon-stock-calculations`, {}, { plotId }, 'GET');
    return isSuccess ? data : [];
  } catch (e) { console.error('getCarbonStockByPlot:', e); return []; }
};

export const createCarbonStock = async (dto) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/carbon-stock-calculations`, {}, clean(dto), 'POST');
    return isSuccess ? data : null;
  } catch (e) { console.error('createCarbonStock:', e); return null; }
};

export const updateCarbonStock = async (id, dto) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/carbon-stock-calculations/${id}`, {}, clean(dto), 'PATCH');
    return isSuccess ? data : null;
  } catch (e) { console.error('updateCarbonStock:', e); return null; }
};

export const deleteCarbonStock = async (id) => {
  try {
    const { isSuccess } = await apiCall(`${BASE}/carbon-stock-calculations/${id}`, {}, {}, 'DELETE');
    return isSuccess;
  } catch (e) { console.error('deleteCarbonStock:', e); return false; }
};

/* ═══════════════════════════════════════════════════════
   MANAGEMENT ACTIVITIES
═══════════════════════════════════════════════════════ */
export const getManagementActivitiesByPlot = async (plotId) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/management-activities`, {}, { plotId }, 'GET');
    return isSuccess ? data : [];
  } catch (e) { console.error('getManagementActivitiesByPlot:', e); return []; }
};

export const createManagementActivity = async (dto) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/management-activities`, {}, clean(dto), 'POST');
    return isSuccess ? data : null;
  } catch (e) { console.error('createManagementActivity:', e); return null; }
};

export const updateManagementActivity = async (id, dto) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/management-activities/${id}`, {}, clean(dto), 'PATCH');
    return isSuccess ? data : null;
  } catch (e) { console.error('updateManagementActivity:', e); return null; }
};

export const deleteManagementActivity = async (id) => {
  try {
    const { isSuccess } = await apiCall(`${BASE}/management-activities/${id}`, {}, {}, 'DELETE');
    return isSuccess;
  } catch (e) { console.error('deleteManagementActivity:', e); return false; }
};

/* ═══════════════════════════════════════════════════════
   HARVEST RECORDS
═══════════════════════════════════════════════════════ */
export const getHarvestRecordsByPlot = async (plotId) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/harvest-records`, {}, { plotId }, 'GET');
    return isSuccess ? data : [];
  } catch (e) { console.error('getHarvestRecordsByPlot:', e); return []; }
};

export const createHarvestRecord = async (dto) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/harvest-records`, {}, clean(dto), 'POST');
    return isSuccess ? data : null;
  } catch (e) { console.error('createHarvestRecord:', e); return null; }
};

export const updateHarvestRecord = async (id, dto) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/harvest-records/${id}`, {}, clean(dto), 'PATCH');
    return isSuccess ? data : null;
  } catch (e) { console.error('updateHarvestRecord:', e); return null; }
};

export const deleteHarvestRecord = async (id) => {
  try {
    const { isSuccess } = await apiCall(`${BASE}/harvest-records/${id}`, {}, {}, 'DELETE');
    return isSuccess;
  } catch (e) { console.error('deleteHarvestRecord:', e); return false; }
};

/* ═══════════════════════════════════════════════════════
   MONITORING SCHEDULES
═══════════════════════════════════════════════════════ */
export const getMonitoringSchedulesByPlot = async (plotId) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/monitoring-schedules`, {}, { plotId }, 'GET');
    return isSuccess ? data : [];
  } catch (e) { console.error('getMonitoringSchedulesByPlot:', e); return []; }
};

export const createMonitoringSchedule = async (dto) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/monitoring-schedules`, {}, clean(dto), 'POST');
    return isSuccess ? data : null;
  } catch (e) { console.error('createMonitoringSchedule:', e); return null; }
};

export const updateMonitoringSchedule = async (id, dto) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/monitoring-schedules/${id}`, {}, clean(dto), 'PATCH');
    return isSuccess ? data : null;
  } catch (e) { console.error('updateMonitoringSchedule:', e); return null; }
};

export const deleteMonitoringSchedule = async (id) => {
  try {
    const { isSuccess } = await apiCall(`${BASE}/monitoring-schedules/${id}`, {}, {}, 'DELETE');
    return isSuccess;
  } catch (e) { console.error('deleteMonitoringSchedule:', e); return false; }
};

/* ═══════════════════════════════════════════════════════
   EMISSION LEAKAGES
   Entity: emissionSource / emissionDate / activityDescription /
           fuelOrMaterialType / quantity / unit /
           estimatedEmissionsTco2e / leakageEstimateTco2e / notes
═══════════════════════════════════════════════════════ */
export const getEmissionLeakagesByPlot = async (plotId) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/emission-leakages`, {}, { plotId }, 'GET');
    return isSuccess ? data : [];
  } catch (e) { console.error('getEmissionLeakagesByPlot:', e); return []; }
};

export const createEmissionLeakage = async (dto) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/emission-leakages`, {}, clean(dto), 'POST');
    return isSuccess ? data : null;
  } catch (e) { console.error('createEmissionLeakage:', e); return null; }
};

export const updateEmissionLeakage = async (id, dto) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/emission-leakages/${id}`, {}, clean(dto), 'PATCH');
    return isSuccess ? data : null;
  } catch (e) { console.error('updateEmissionLeakage:', e); return null; }
};

export const deleteEmissionLeakage = async (id) => {
  try {
    const { isSuccess } = await apiCall(`${BASE}/emission-leakages/${id}`, {}, {}, 'DELETE');
    return isSuccess;
  } catch (e) { console.error('deleteEmissionLeakage:', e); return false; }
};

/* ═══════════════════════════════════════════════════════
   BIODIVERSITY OBSERVATIONS
═══════════════════════════════════════════════════════ */
export const getBiodiversityByPlot = async (plotId) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/biodiversity-observations`, {}, { plotId }, 'GET');
    return isSuccess ? data : [];
  } catch (e) { console.error('getBiodiversityByPlot:', e); return []; }
};

export const createBiodiversityObservation = async (dto) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/biodiversity-observations`, {}, clean(dto), 'POST');
    return isSuccess ? data : null;
  } catch (e) { console.error('createBiodiversityObservation:', e); return null; }
};

export const updateBiodiversityObservation = async (id, dto) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/biodiversity-observations/${id}`, {}, clean(dto), 'PATCH');
    return isSuccess ? data : null;
  } catch (e) { console.error('updateBiodiversityObservation:', e); return null; }
};

export const deleteBiodiversityObservation = async (id) => {
  try {
    const { isSuccess } = await apiCall(`${BASE}/biodiversity-observations/${id}`, {}, {}, 'DELETE');
    return isSuccess;
  } catch (e) { console.error('deleteBiodiversityObservation:', e); return false; }
};

/* ═══════════════════════════════════════════════════════
   STRATUM QC
═══════════════════════════════════════════════════════ */
export const getStratumQCByStratum = async (stratumId) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/stratum-qcs`, {}, { stratumId }, 'GET');
    return isSuccess ? data : [];
  } catch (e) { console.error('getStratumQCByStratum:', e); return []; }
};

export const createStratumQC = async (dto) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/stratum-qcs`, {}, clean(dto), 'POST');
    return isSuccess ? data : null;
  } catch (e) { console.error('createStratumQC:', e); return null; }
};

export const updateStratumQC = async (id, dto) => {
  try {
    const { isSuccess, data } = await apiCall(`${BASE}/stratum-qcs/${id}`, {}, clean(dto), 'PATCH');
    return isSuccess ? data : null;
  } catch (e) { console.error('updateStratumQC:', e); return null; }
};

export const deleteStratumQC = async (id) => {
  try {
    const { isSuccess } = await apiCall(`${BASE}/stratum-qcs/${id}`, {}, {}, 'DELETE');
    return isSuccess;
  } catch (e) { console.error('deleteStratumQC:', e); return false; }
};

/* ═══════════════════════════════════════════════════════
   ENTITY DOCUMENTS  (images + evidence files)
   POST  multipart/form-data — file + DTO fields
   GET   ?entityType=PLOT&entityId=1
   DELETE /:id
═══════════════════════════════════════════════════════ */
export const getEntityDocuments = async (entityType, entityId) => {
  try {
    const { isSuccess, data } = await apiCall(
      `${BASE}/entity-documents`,
      {},
      { entityType, entityId },
      'GET'
    );
    return isSuccess ? data : [];
  } catch (e) { console.error('getEntityDocuments:', e); return []; }
};

/**
 * Upload a file linked to an entity.
 * @param {File}   file         — browser File object (from <input type="file">)
 * @param {object} dto          — { documentType, entityType, entityId, kpi?, documentDate?, gpsLocation?, notes? }
 */
export const uploadEntityDocument = async (file, dto) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    Object.entries(clean(dto)).forEach(([k, v]) => formData.append(k, v));

    const { isSuccess, data } = await apiCall(
      `${BASE}/entity-documents`,
      { 'Content-Type': 'multipart/form-data' },
      formData,
      'POST'
    );
    return isSuccess ? data : null;
  } catch (e) { console.error('uploadEntityDocument:', e); return null; }
};

export const deleteEntityDocument = async (id) => {
  try {
    const { isSuccess } = await apiCall(`${BASE}/entity-documents/${id}`, {}, {}, 'DELETE');
    return isSuccess;
  } catch (e) { console.error('deleteEntityDocument:', e); return false; }
};